import { inject, Injectable } from '@angular/core';
import { User } from 'app/core/user/user.types';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { MacroService } from '../../macro.service';

@Injectable({ providedIn: 'root' })
export class UserService {
    private _macroService = inject(MacroService);

    /**
     * Stream interno che emette l'utente corrente.
     * ReplaySubject(1) permette ai nuovi subscriber di ricevere
     * immediatamente l'ultimo valore emesso.
     */
    private _userSubject = new ReplaySubject<User>(1);

    /**
     * Cache sincrona dell'utente corrente.
     *
     * Serve per permettere accesso immediato allo user senza subscribe
     *
     * ReplaySubject non espone il valore corrente, quindi manteniamo
     * una copia locale aggiornata ogni volta che lo user cambia.
     */
    private _userSync?: User;

    /**
     * Setter dello user corrente.
     *
     * Effetti:
     * 1) aggiorna la cache sincrona
     * 2) emette il nuovo valore nello stream
     */
    set user(value: User) {
        this._userSync = value;
        this._userSubject.next(value);

        // carico company id in macroMap
        if (value?.company?.company_id != null) {
            this._macroService.patchMacros({
                'USER.COMPANY_ID': String(value.company.company_id)
            });
        }
    }

    /**
     * Getter sincrono dello user corrente.
     *
     * Lancia errore se lo user non è ancora stato caricato.
     * Utile quando la presenza dello user è obbligatoria.
     */
    get user(): User {
        if (!this._userSync) {
            throw new Error('User not loaded yet');
        }
        return this._userSync;
    }

    /**
     * Getter sincrono "safe".
     *
     * Restituisce:
     * - lo user corrente se disponibile
     * - null se non ancora caricato
     *
     * Utile nelle fasi iniziali dell'app (bootstrap / resolver).
     */
    get userOrNull(): User | null {
        return this._userSync ?? null;
    }

    /**
     * Observable pubblico dello user corrente.
     * I componenti o servizi possono sottoscriversi per reagire ai cambiamenti.
     */
    get user$(): Observable<User> {
        return this._userSubject.asObservable();
    }

}
